import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { Button, Form, Select, Space } from 'antd';
import { CollectionSortRuleInDirection } from '@/api/generated/schemas';
import { useSortFields } from './useSortFields';

interface Props {
  /** Disable the whole section (e.g. system-managed records). */
  disabled?: boolean;
}

/**
 * Editable list of sort rules for a collection's publications. Rules apply in
 * order, so the second one only breaks ties left by the first.
 *
 * Field options come from the backend (`useSortFields`); scoped fields such as
 * `series_position` are excluded there and rejected by the API.
 */
export const CollectionSortingList = ({ disabled }: Props) => {
  const t = useTranslate();
  const { options, isLoading } = useSortFields();

  return (
    <Form.List name="publication_custom_sorting">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...rest }) => (
            <Space key={key} align="baseline" style={{ display: 'flex' }}>
              <Form.Item {...rest} name={[name, 'field']}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ minWidth: 220 }}
                  disabled={disabled}
                  loading={isLoading}
                  placeholder={t('base.collection.sorting_field')}
                  options={options}
                />
              </Form.Item>
              <Form.Item
                {...rest}
                name={[name, 'direction']}
                initialValue={CollectionSortRuleInDirection.asc}
              >
                <Select
                  style={{ minWidth: 140 }}
                  disabled={disabled}
                  placeholder={t('base.collection.sorting_direction')}
                  options={Object.values(CollectionSortRuleInDirection).map(
                    (value) => ({
                      label: t(`base.collection.direction.${value}`),
                      value,
                    })
                  )}
                />
              </Form.Item>
              {!disabled && (
                <MinusCircleOutlined
                  aria-label={t('base.collection.sorting_remove')}
                  onClick={() => remove(name)}
                />
              )}
            </Space>
          ))}
          {!disabled && (
            <Form.Item>
              <Button
                type="dashed"
                block
                icon={<PlusOutlined />}
                onClick={() =>
                  add({ direction: CollectionSortRuleInDirection.asc })
                }
              >
                {t('base.collection.sorting_add')}
              </Button>
            </Form.Item>
          )}
        </>
      )}
    </Form.List>
  );
};
